import { cartEvent, cartFromSections } from './clauspop-cart.js';

// One deadline per tab/cart session, preserved across drawer hydration and reloads.
const deadlines = new Map();
class ClausPopCartProgress extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    const { signal } = this.abort;
    document.addEventListener('clauspop:cart', e => this.applyCart(e.detail), { signal });
    document.addEventListener(cartEvent, e => {
      e.promise?.then(({ detail }) => {
        const cart = detail?.ajaxCart || cartFromSections(detail?.sections);
        if (cart) this.applyCart(cart);
        else if (!detail?.didError) this.fetchCart();
      }).catch(() => {});
    }, { signal });
    // Hydration can insert children after connection, or replace only children
    // while keeping all cart attributes unchanged. Repaint in either case.
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.scheduleRender());
    this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-total','data-count','data-threshold','data-minutes'] });
    clearInterval(this.clock);
    this.clock = setInterval(() => this.tick(), 1000);
    this.render();
    this.scheduleRender();
  }
  scheduleRender() {
    cancelAnimationFrame(this.renderFrame);
    this.renderFrame = requestAnimationFrame(() => { if (this.isConnected) this.render(); });
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); clearInterval(this.clock); cancelAnimationFrame(this.renderFrame); }
  async fetchCart() {
    const request = (this.request || 0) + 1; this.request = request;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: this.abort.signal });
      if (response.ok) { const cart = await response.json(); if (this.request === request) this.applyCart(cart); }
    } catch { /* Server-rendered progress stays usable if the read fails. */ }
  }
  applyCart(cart) {
    if (!cart || typeof cart.total_price !== 'number') return;
    this.request = (this.request || 0) + 1; // Ignore an older fallback read after a fresh mutation.
    this.dataset.total = String(cart.total_price);
    this.dataset.count = String(cart.item_count ?? cart.items?.reduce((n,i) => n+i.quantity,0) ?? 0);
    if (typeof cart.currency === 'string') this.dataset.currency = cart.currency;
    this.render();
  }
  render() {
    const total = Number(this.dataset.total) || 0, threshold = Number(this.dataset.threshold) || 0;
    const count = Number(this.dataset.count) || 0;
    this.hidden = count === 0;
    this.key = `clauspop-countdown:${location.host}:${this.dataset.minutes}`;
    if (count === 0) {
      deadlines.delete(this.key); try { sessionStorage.removeItem(this.key); } catch {}
      this.deadline = null;
    } else if (this.querySelector('[data-cart-timer]')) {
      let saved = deadlines.get(this.key);
      try { saved = Number(sessionStorage.getItem(this.key)) || saved; } catch {}
      this.deadline = saved || Date.now() + Math.max(1, Number(this.dataset.minutes) || 10) * 60000;
      deadlines.set(this.key, this.deadline);
      try { sessionStorage.setItem(this.key, String(this.deadline)); } catch {}
    }
    const bar = this.querySelector('[role=progressbar]');
    if (bar && threshold > 0) {
      const percent = Math.min(100, Math.max(0, total / threshold * 100));
      this.classList.toggle('is-unlocked', total >= threshold);
      bar.setAttribute('aria-valuenow', String(Math.round(percent)));
      const fill = this.querySelector('[data-shipping-fill]');
      if (fill) fill.style.width = `${percent}%`;
      let remaining;
      try { remaining = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(Math.max(0, threshold-total)/100); }
      catch { remaining = (Math.max(0,threshold-total)/100).toFixed(2); }
      const message = this.querySelector('[data-shipping-message]');
      const text = total >= threshold ? this.dataset.success || 'Congrats! You get FREE shipping!' : (this.dataset.before || 'Add [amount] to unlock free shipping!').replaceAll('[amount]', remaining);
      if (message && message.textContent !== text) message.textContent = text;
    }
    this.tick();
  }
  tick() {
    const counter = this.querySelector('[data-countdown]');
    if (!counter || !this.deadline) return;
    const seconds = Math.max(0, Math.ceil((this.deadline-Date.now())/1000));
    const label = this.querySelector('[data-timer-label]');
    const text = (seconds ? this.dataset.timerLabel : this.dataset.timerEnd) || '';
    if (label && label.textContent !== text) label.textContent = text;
    const suffix = this.querySelector('[data-timer-suffix]');
    if (suffix) suffix.hidden = !seconds;
    const time = seconds ? `${String(Math.floor(seconds/60)).padStart(2,'0')}:${String(seconds%60).padStart(2,'0')}` : '';
    if (counter.textContent !== time) counter.textContent = time;
  }
}
if (!customElements.get('cp-cart-progress')) customElements.define('cp-cart-progress', ClausPopCartProgress);
